import React from "react";
import { formatMobile } from "../../../../../../shared/utils/format-mobile";
import type {
  ShopEntity,
  ShopFooterDesign,
} from "../../../../../../domain/types/company-shop";
import type { CompanyInfoResponse } from "../../../../../../domain/types/shared";

interface PreviewFooterProps {
  companyInfo: CompanyInfoResponse | undefined;
  shopEntityData: ShopEntity | null;
  footerDesign: ShopFooterDesign;
}

export const PreviewFooter = ({
  companyInfo,
  shopEntityData,
  footerDesign,
}: PreviewFooterProps) => {
  const visibleMenuItems = [...footerDesign.menuItems]
    .filter((item) => item.visible)
    .sort((a, b) => a.displayOrder - b.displayOrder);

  const hasSnsLink =
    shopEntityData?.youtubeUrl ||
    shopEntityData?.facebookUrl ||
    shopEntityData?.instagramUrl ||
    shopEntityData?.naverBlogUrl ||
    shopEntityData?.kakaoChannelUrl;

  return (
    <div
      className="w-full aspect-[3/1]
              flex flex-col lg:flex-row lg:justify-between lg:items-center
              px-[20px] py-[40px] gap-10 lg:gap-0"
      style={{
        backgroundColor: footerDesign.backgroundColor,
        color: footerDesign.textColor,
      }}
    >
      <div className="flex flex-col text-start">
        <h4 className="text-lg">이용안내</h4>
        <div className="mb-4">
          <h3 className="text-xl font-bold">
            {formatMobile(companyInfo?.phone ?? "")}
          </h3>
          {companyInfo?.secondaryContactType ? (
            <div>
              {companyInfo?.secondaryContactType}:{" "}
              {companyInfo?.secondaryContact}
            </div>
          ) : null}
        </div>

        {footerDesign.showBusinessHours &&
        (companyInfo?.businessHours ||
          companyInfo?.lunchBreak ||
          companyInfo?.businessHoursNotice) ? (
          <div className="flex flex-col">
            <div>
              평일 {companyInfo.businessHours}
              {companyInfo?.lunchBreak || companyInfo.businessHoursNotice ? (
                <div>
                  {companyInfo?.lunchBreak ? (
                    <span>점심 {companyInfo.lunchBreak}</span>
                  ) : null}{" "}
                  {companyInfo?.businessHoursNotice && (
                    <span>({companyInfo?.businessHoursNotice})</span>
                  )}
                </div>
              ) : null}
            </div>
          </div>
        ) : null}

        {footerDesign.showSns && hasSnsLink ? (
          <>
            <div className="mb-4"></div>
            <div className="flex gap-4">
              {shopEntityData?.youtubeUrl && (
                <div>
                  <img
                    className="w-6"
                    src="/assets/icons/youtube.svg"
                    alt="유튜브 로고"
                  />
                </div>
              )}
              {shopEntityData?.facebookUrl && (
                <div>
                  <img
                    className="w-6"
                    src="/assets/icons/facebook.svg"
                    alt="페이스북 로고"
                  />
                </div>
              )}
              {shopEntityData?.instagramUrl && (
                <div>
                  <img
                    className="w-6"
                    src="/assets/icons/instagram.svg"
                    alt="인스타그램 로고"
                  />
                </div>
              )}
              {shopEntityData?.naverBlogUrl && (
                <div>
                  <img
                    className="w-6"
                    src="/assets/icons/naverBlog.svg"
                    alt="네이버 블로그 로고"
                  />
                </div>
              )}
              {shopEntityData?.kakaoChannelUrl && (
                <div>
                  <img
                    className="w-6"
                    src="/assets/icons/kakao-icon.svg"
                    alt="카카오 채널 로고"
                  />
                </div>
              )}
            </div>
          </>
        ) : null}
      </div>
      <div className="flex flex-col text-start">
        <div className="flex flex-row flex-wrap gap-4 mb-4">
          {visibleMenuItems.map((item) => (
            <div key={item.key}>{item.label}</div>
          ))}
        </div>

        {footerDesign.showBusinessInfo ? (
          <>
            <div>
              {companyInfo?.companyName} / 대표: {companyInfo?.ceoName}
            </div>
            <div>
              주소: {companyInfo?.address}, {companyInfo?.addressDetail}
            </div>
            <div>
              사업자등록번호: {companyInfo?.businessNumber} / 통신판매신고번호:{" "}
              {companyInfo?.ecommerceBizNumber}
            </div>
            {/* 추가 사업자 정보(표시 전용 자유 텍스트). 입력한 줄 수만큼 출력하며 비어 있으면 줄이 생기지 않는다 */}
            {(companyInfo?.additionalBusinessInfo ?? "")
              .split(/\r?\n/)
              .map((line) => line.trim())
              .filter((line) => line.length > 0)
              .map((line, index) => (
                <div key={index}>{line}</div>
              ))}
          </>
        ) : null}
      </div>
    </div>
  );
};
